import { useEffect, useRef, useSyncExternalStore } from 'react';

/*
  One central "unsaved changes" signal for the whole app.

  A module-level registry of "dirty source" ids (one per open, edited form)
  feeds three consumers:
    1. a `beforeunload` warning      (src/guards/UnsavedChangesGuard.tsx)
    2. an in-app react-router block  (src/guards/UnsavedChangesGuard.tsx)
    3. PWA service-worker update deferral (src/pwa/pwaUpdate.ts)

  Forms register via the `useUnsavedChanges(isDirty)` hook; the consumers read
  `hasUnsavedChanges()` (or subscribe for reactive UI).

  SECURITY ESCAPE HATCH: a forced sign-out (idle timeout or manual sign-out)
  redirects via `window.location.assign`, which `beforeunload` would interrupt
  with a native "leave site?" dialog. `suppressGuard()` is called immediately
  before any such redirect so a security logout is never blocked or prompted.
  Suppression is one-way for the page's lifetime — the page is navigating away.
*/

const dirtySources = new Set<string>();
const listeners = new Set<() => void>();
let suppressed = false;

function emit() {
  for (const fn of listeners) fn();
}

/** Mark a source (form) as having unsaved changes. */
export function markDirty(id: string): void {
  if (!dirtySources.has(id)) {
    dirtySources.add(id);
    emit();
  }
}

/** Clear a source's unsaved-changes flag. */
export function markClean(id: string): void {
  if (dirtySources.delete(id)) {
    emit();
  }
}

/**
 * True when at least one source has unsaved changes AND the guard has not been
 * suppressed for a security sign-out. All three consumers gate on this, so
 * suppression uniformly silences every prompt.
 */
export function hasUnsavedChanges(): boolean {
  return !suppressed && dirtySources.size > 0;
}

/**
 * Permanently silence the guard for the rest of this page's lifetime. Call
 * right before a sign-out redirect so a forced logout is never blocked.
 */
export function suppressGuard(): void {
  if (!suppressed) {
    suppressed = true;
    emit();
  }
}

/** Subscribe to registry changes (for reactive UI / the PWA updater). */
export function subscribeUnsavedChanges(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Test-only: reset module state between cases. */
export function __resetUnsavedChanges(): void {
  dirtySources.clear();
  listeners.clear();
  suppressed = false;
}

let nextId = 0;

/**
 * Register this component as a dirty source while `isDirty` is true. Clears the
 * flag when `isDirty` goes false and on unmount, so a saved or cancelled form
 * never leaves a stale signal behind.
 */
export function useUnsavedChanges(isDirty: boolean): void {
  const idRef = useRef<string | undefined>(undefined);
  if (idRef.current === undefined) idRef.current = `uc-${nextId++}`;

  useEffect(() => {
    const id = idRef.current!;
    if (isDirty) markDirty(id);
    else markClean(id);
  }, [isDirty]);

  // Always clear on unmount (covers the case where the form unmounts while
  // still dirty — e.g. a successful save that closes the panel).
  useEffect(() => {
    const id = idRef.current!;
    return () => markClean(id);
  }, []);
}

/** Reactive read of `hasUnsavedChanges()` for React components. */
export function useHasUnsavedChanges(): boolean {
  return useSyncExternalStore(subscribeUnsavedChanges, hasUnsavedChanges, hasUnsavedChanges);
}
